import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/context/LanguageContext";

interface ProPlayer {
  id: string;
  name: string;
  gender: "male" | "female";
  rank: number | null;
  photo_url: string | null;
  country: string | null;
  is_active: boolean;
  sort_order: number;
  cta_label: string | null;
  cta_url: string | null;
  slug: string | null;
}

const TopProPlayers = () => {
  const { lang } = useLanguage();
  const [tab, setTab] = useState<"male" | "female">("male");

  const { data: players = [], isLoading } = useQuery({
    queryKey: ["pro-players-active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pro_players" as any)
        .select("id,name,gender,rank,photo_url,country,is_active,sort_order,cta_label,cta_url,slug")
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .order("rank", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as unknown as ProPlayer[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const filtered = useMemo(
    () => players.filter((p) => p.gender === tab).slice(0, 10),
    [players, tab]
  );

  const t = (lv: string, en: string) => (lang === "lv" ? lv : en);

  const countryToIso2 = (raw: string): string | null => {
    const v = raw.trim().toLowerCase();
    const map: Record<string, string> = {
      arg: "ar", argentina: "ar",
      esp: "es", spa: "es", spain: "es", espana: "es", "españa": "es",
      por: "pt", prt: "pt", portugal: "pt",
      lva: "lv", lat: "lv", latvia: "lv", lv: "lv",
      bra: "br", brazil: "br",
      mex: "mx", mexico: "mx",
      fra: "fr", france: "fr",
      ita: "it", italy: "it",
      usa: "us", "united states": "us",
      gbr: "gb", uk: "gb", "united kingdom": "gb",
      chl: "cl", chile: "cl",
      par: "py", paraguay: "py",
      uru: "uy", uruguay: "uy",
    };
    if (map[v]) return map[v];
    if (v.length === 2) return v;
    return null;
  };

  const renderCard = (p: ProPlayer, highlight: boolean) => {
    const href = p.slug
      ? `/${lang}/players/${p.slug}`
      : (p.cta_url || `/${lang}/raketes`);
    const iso = p.country ? countryToIso2(p.country) : null;
    return (
      <a
        key={p.id}
        href={href}
        className={cn(
          "group relative flex flex-col rounded-xl overflow-hidden border shadow-sm hover:shadow-md transition-all h-full",
          highlight
            ? "bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 border-amber-300 ring-1 ring-amber-300/60"
            : "bg-card"
        )}
      >
        <div className={cn("relative flex-1 min-h-0 overflow-hidden", highlight ? "bg-amber-50/50" : "bg-muted")}>
          {p.photo_url ? (
            <img
              src={p.photo_url}
              alt={p.name}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-2xl font-display text-muted-foreground">
              {p.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </div>
          )}
          {p.rank ? (
            <div className={cn(
              "absolute top-1.5 left-1.5 font-bold rounded-full flex items-center justify-center shadow h-6 min-w-6 px-1.5 text-[11px]",
              highlight ? "bg-amber-500 text-white" : "bg-primary text-primary-foreground"
            )}>
              #{p.rank}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5 min-w-0 p-2">
          {iso ? (
            <img
              src={`https://hatscripts.github.io/circle-flags/flags/${iso}.svg`}
              alt={p.country ?? ""}
              loading="lazy"
              className="w-4 h-4 rounded-full shrink-0 object-cover"
            />
          ) : null}
          <p className="font-semibold leading-tight break-words text-[11px] md:text-xs truncate">{p.name}</p>
        </div>
      </a>
    );
  };

  return (
    <section className="container mx-auto px-4 py-10 md:py-14">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-bold">
            {t("Top Pro Spēlētāji", "Top Pro Players")}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t("Pasaules labākie padel spēlētāji", "The world's best padel players")}
          </p>
        </div>
        <div className="inline-flex rounded-full border bg-card p-1 self-start">
          <button
            type="button"
            onClick={() => setTab("male")}
            className={cn(
              "px-4 py-1.5 rounded-full text-sm font-semibold transition-colors",
              tab === "male" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            {t("Vīrieši", "Men")}
          </button>
          <button
            type="button"
            onClick={() => setTab("female")}
            className={cn(
              "px-4 py-1.5 rounded-full text-sm font-semibold transition-colors",
              tab === "female" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            {t("Sievietes", "Women")}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="rounded-xl aspect-[3/4]" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6">{t("Nav pievienoti spēlētāji.", "No players yet.")}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3">
          {filtered.map((p, i) => (
            <div key={p.id} className="aspect-[3/4]">
              {renderCard(p, i < 2)}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default TopProPlayers;
